import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/AuthContext'
import { CURRENCY_CODES } from '@/lib/currency'
import type { Household } from '@/types/db'

// Provider central de household (TENANT). TODA query recebe household_id daqui.
// Multi-tenant: o household ATIVO é resolvido a partir da sessão —
//   • usuário comum: seu household em household_members (auth.uid()).
//   • super-admin (gismario): pode ALTERNAR entre qualquer tenant (switcher).
// A escolha do super-admin fica em localStorage para persistir entre sessões.
const ACTIVE_KEY = 'easymoney.active-household'

export interface TenantRef {
  id: string
  name: string
}

interface HouseholdContextValue {
  /** Tenant ATIVO. '' apenas no breve intervalo antes de resolver o membership. */
  householdId: string
  /** ID do usuário logado (auth.uid). */
  userId: string | null
  /** É o super-admin geral? (administra todos os tenants) */
  isSuperAdmin: boolean
  /** Tenants que o usuário pode ver/alternar (super-admin = todos). */
  availableHouseholds: TenantRef[]
  /** Troca o tenant ativo (super-admin, ou multi-household futuro). */
  setActiveHousehold: (id: string) => void
  household: Household | null
  baseCurrency: string
  /** Moedas habilitadas (códigos). Fallback: catálogo inteiro se ainda não carregou. */
  enabledCurrencies: string[]
  /** Idioma do usuário logado ('pt-BR' | 'en' | 'es'). Vem de profiles.locale (por usuário). */
  language: string
  loading: boolean
  refresh: () => Promise<void>
}

const HouseholdContext = createContext<HouseholdContextValue | null>(null)

export function HouseholdProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const userId = session?.user?.id ?? null

  const [household, setHousehold] = useState<Household | null>(null)
  const [profileLocale, setProfileLocale] = useState<string | null>(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [availableHouseholds, setAvailableHouseholds] = useState<TenantRef[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  function setActiveHousehold(id: string) {
    localStorage.setItem(ACTIVE_KEY, id)
    setActiveId(id)
  }

  async function refresh() {
    if (!userId) {
      setLoading(false)
      return
    }
    // 1) Perfil (idioma + flag super-admin) e memberships do usuário.
    const [profRes, memRes] = await Promise.all([
      supabase.from('profiles').select('locale, is_super_admin').eq('user_id', userId).maybeSingle(),
      supabase.from('household_members').select('household_id, role').eq('user_id', userId),
    ])
    const prof = profRes.data as { locale?: string; is_super_admin?: boolean } | null
    setProfileLocale(prof?.locale ?? null)
    const superAdmin = !!prof?.is_super_admin
    setIsSuperAdmin(superAdmin)

    const myIds = (memRes.data ?? []).map((m: { household_id: string }) => m.household_id)

    // 2) Tenants disponíveis = APENAS os do próprio membership (mesmo p/ super-admin:
    //    super-admin gere USUÁRIOS de todos os tenants, mas NÃO vê os DADOS de outro
    //    tenant — isolamento total, garantido pela RLS). O switcher só aparece se a
    //    pessoa pertencer a mais de um tenant (multi-household futuro).
    let tenants: TenantRef[] = []
    if (myIds.length) {
      const { data } = await supabase.from('households').select('id, name').in('id', myIds)
      tenants = (data ?? []) as TenantRef[]
    }
    setAvailableHouseholds(tenants)

    // 3) Tenant ativo: escolha salva (se for de um tenant que a pessoa pertence) → 1º membership.
    const stored = localStorage.getItem(ACTIVE_KEY)
    const active = stored && myIds.includes(stored) ? stored : myIds[0] ?? null
    setActiveId(active)

    // 4) Carrega o household ativo.
    if (active) {
      const { data } = await supabase.from('households').select('*').eq('id', active).maybeSingle()
      setHousehold(data ?? null)
    } else {
      setHousehold(null)
    }
    setLoading(false)
  }

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, activeId])

  const value: HouseholdContextValue = {
    householdId: activeId ?? '',
    userId,
    isSuperAdmin,
    availableHouseholds,
    setActiveHousehold,
    household,
    baseCurrency: household?.base_currency ?? 'GTQ',
    enabledCurrencies:
      household?.enabled_currencies && household.enabled_currencies.length > 0
        ? household.enabled_currencies
        : CURRENCY_CODES,
    language: profileLocale ?? household?.language ?? 'pt-BR',
    loading,
    refresh,
  }

  return <HouseholdContext.Provider value={value}>{children}</HouseholdContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHousehold() {
  const ctx = useContext(HouseholdContext)
  if (!ctx) throw new Error('useHousehold deve ser usado dentro de HouseholdProvider')
  return ctx
}
